import {
    Component,
    Host,
    Event,
    Prop,
    Element,
    EventEmitter,
    Listen,
    h,
    Watch,
} from "@stencil/core";
import applyElementPolyfills from "../../polyfills/element";
applyElementPolyfills();

@Component({
    tag: "ux-iws-slider-slide-zoomable",
    styleUrl: "slider-slide-zoomable.scss",
    shadow: false,
})
export class SlideZoomable {
    @Element() element: HTMLUxIwsSliderSlideZoomableElement;
    private slideContent: HTMLElement;
    private zoomImageBlock: HTMLElement;
    private zoomImage: HTMLImageElement;
    private zoomButton: HTMLDivElement;
    private slider: HTMLUxSliderElement;

    /** Manages the zoomed state of the slides,
     * Will be updated automatically by the component also for the sliblin slides,
     * do not set manually unless it is a desired result to have the slider to start
     * from a zoomed state.
     **/
    @Prop({ mutable: true }) isZoomed: boolean = false;

    private slideDimensions: DOMRect;

    /** updates anchor pagination position on activation of zoomed state */
    @Event({
        eventName: "uxSliderUpdatePagination",
        composed: true,
        cancelable: true,
        bubbles: true,
    })
    updatePagination: EventEmitter;

    private onZoomButtonClick = async (event: Event) => {
        const target = event.target as HTMLElement;
        if (
            target.hasAttribute("data-ux-slider-slide-zoom") ||
            target.closest("a").hasAttribute("data-ux-slider-slide-zoom")
        ) {
            event.preventDefault();
            if (!this.isZoomed) {
                this.isZoomed = true;
            } else {
                this.isZoomed = false;
            }
        }
    };

    @Watch("isZoomed")
    async addZoomedState() {
        if (this.isZoomed === false) return;
        this.zoomButton.style.left = `${this.zoomButton.offsetLeft}px`;
        this.zoomButton.style.top = `${this.zoomButton.offsetTop}px`;
        this.zoomButton.style.bottom = "auto";
        this.zoomButton.style.maxHeight = "38px";
        this.zoomButton.style.overflow = "hidden";
        this.slideDimensions = this.element.getBoundingClientRect();
        this.slideContent.classList.add("is-zoomed");
        this.zoomImageBlock.style.height = `${this.slideDimensions.height}px`;
        this.zoomImageBlock.style.width = `${this.slideDimensions.width}px`;
        await this.slider.updateSoft();
        this.updatePagination.emit({
            id: this.slider.getAttribute("slider-id"),
        });
        this.zoomImageBlock.addEventListener(
            "mousemove",
            this.moveImageOnHover
        );
        this.updateSlidesZoomedProp();
    }

    @Watch("isZoomed")
    removeZoomedState() {
        if (this.isZoomed === true) return;
        if (!this.slideContent) return;
        this.slideContent.classList.remove("is-zoomed");
        this.zoomImageBlock.removeAttribute("style");
        this.zoomButton.removeAttribute("style");
        this.isZoomed = false;
        this.updatePagination.emit({
            id: this.slider.getAttribute("slider-id"),
        });
        this.zoomImageBlock.removeEventListener(
            "mousemove",
            this.moveImageOnHover
        );
        this.updateSlidesZoomedProp();
    }

    private updateSlidesZoomedProp = () => {
        const sliblingsSlides = this.slider.querySelectorAll(
            "ux-iws-slider-slide-zoomable"
        );
        if (this.isZoomed === true) {
            Array.from(sliblingsSlides).forEach((slide) => {
                slide.setAttribute("is-zoomed", "");
            });
        } else {
            Array.from(sliblingsSlides).forEach((slide) => {
                slide.removeAttribute("is-zoomed");
            });
        }
    };

    private classObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (
                mutation.type !== "attributes" &&
                mutation.attributeName !== "class"
            )
                return;
            if (!this.element.classList.contains("ux-slider-slide-active"))
                this.removeZoomedState();
        });
    });

    private moveImageOnHover = (event) => {
        const contentWidth = this.zoomImage.getBoundingClientRect().width;
        const contentHeight = this.zoomImage.getBoundingClientRect().height;
        const diffX = contentWidth - this.slideDimensions.width;
        const diffY = contentHeight - this.slideDimensions.height;
        const view = this.zoomImageBlock.ownerDocument.defaultView;
        const pointerX =
            event.pageX -
            (this.zoomImageBlock.getBoundingClientRect().left +
                view.pageXOffset);
        const pointerY =
            event.pageY -
            (this.zoomImageBlock.getBoundingClientRect().top +
                view.pageYOffset);
        const offsetX = (pointerX / this.slideDimensions.width) * diffX;
        const offsetY = (pointerY / this.slideDimensions.height) * diffY;
        this.zoomImage.style.left = `${-offsetX}px`;
        this.zoomImage.style.top = `${-offsetY}px`;
    };

    connectedCallback() {
        this.element.addEventListener("click", this.onZoomButtonClick);
        window.addEventListener("resize", this.removeZoomedState);
        this.classObserver.observe(this.element, { attributes: true });
    }

    componentDidLoad() {
        this.slideContent = this.element.querySelector(".block-hero");
        if (!this.slideContent) return;
        this.zoomImageBlock = this.element.querySelector(".block-zoom-image");
        this.zoomImage = this.zoomImageBlock.querySelector("img");
        this.zoomButton = this.element.querySelector(".btn-zoom-wrapper");
        this.slider = this.element.closest("ux-slider");
    }

    @Listen("resize", { target: "window" })
    disableZoomOnResize() {
        this.isZoomed = false;
    }

    disconnectedCallback() {
        if (!this.slideContent) return;
        this.element.removeEventListener("click", this.onZoomButtonClick);
        window.removeEventListener("resize", this.removeZoomedState);
    }

    render() {
        return (
            <Host class="ux-slider-slide">
                <slot />
            </Host>
        );
    }
}
